import React, { useState, useEffect, useCallback, useRef } from 'react';
import useSaleReportData from './hooks/useSaleReportData';
import PageToggle from './components/PageToggle';
import CoverSection from './components/CoverSection';
import ProfileSection from './components/ProfileSection';
import LocationSection from './components/LocationSection';
import LandSection from './components/LandSection';
import BasicBuildingSection from './components/BasicBuildingSection';
import DescriptionSection from './components/DescriptionSection';
import GallerySection from './components/GallerySection';
import ContactSection from './components/ContactSection';
import { requestReport, checkReportStatus } from '../../services/saleReport/saleReportService';
import PdfCover from './pdf/PdfCover';
import PdfProfile from './pdf/PdfProfile';
import PdfLocation from './pdf/PdfLocation';
import PdfLand from './pdf/PdfLand';
import PdfBuilding from './pdf/PdfBuilding';
import PdfDescription from './pdf/PdfDescription';
import PdfGallery, { GALLERY_PER_PAGE } from './pdf/PdfGallery';
import PdfContact from './pdf/PdfContact';
import PDF_STYLES from './pdf/PdfStyles';
import { createMeasureContainer, destroyMeasureContainer, splitDescriptionParas } from './pdf/measureDescription';
import {
  buildProfileAddressRows, buildProfilePriceRows, buildProfilePdfPriceRow, buildProfileInfoRows,
  buildLandRows, buildBasicRows, buildBuildingRows, buildDetailRows, buildContactRows,
  resolveToggleBtnLabel, fmtNum,
} from './fieldBuilders';
import styles from './SaleReportPage.module.scss';


const DEFAULT_PAGES = {
  cover: { enabled: true, title: '', fields: {} },
  profile: { enabled: true, fields: {}, sectionInfo: true },
  location: { enabled: true, mapImage: null, cadastralImage: null },
  land: { enabled: true, fields: {} },
  basicBuilding: { enabled: true, fields: {} },
  description: { enabled: true, title: '', body: '' },
  gallery: { enabled: true, layout: null, photos: null },
  contact: { enabled: true, fields: {} },
};

function SaleReportPage({ saleUuid, onClose }) {
  const { loading, error, sale, photos, agent, reportImages } = useSaleReportData(saleUuid);
  const [pages, setPages] = useState(DEFAULT_PAGES);
  const [generating, setGenerating] = useState(false);
  const [lastDownloadUrl, setLastDownloadUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [descPreviewChunks, setDescPreviewChunks] = useState(null);
  const measureContainerRef = useRef(null);

  // 측정 컨테이너 — 마운트 시 생성, 언마운트 시 제거
  useEffect(() => {
    measureContainerRef.current = createMeasureContainer();
    return () => {
      destroyMeasureContainer(measureContainerRef.current);
      measureContainerRef.current = null;
    };
  }, []);

  // description body/title 변경 시 DOM 측정으로 페이지 분할
  useEffect(() => {
    const desc = previewData?.description;
    if (!desc || !measureContainerRef.current) {
      setDescPreviewChunks(null);
      return;
    }
    let cancelled = false;
    const officeName = previewData.officeName || '';
    const now = new Date();
    const dateLabel = `${now.getFullYear()} · ${String(now.getMonth() + 1).padStart(2, '0')}`;
    splitDescriptionParas(desc, officeName, dateLabel, measureContainerRef.current)
      .then((chunks) => { if (!cancelled) setDescPreviewChunks(chunks); })
      .catch(() => { if (!cancelled) setDescPreviewChunks([[]]); });
    return () => { cancelled = true; };
  }, [previewData?.description?.body, previewData?.description?.title, previewData?.officeName]);

  // 뒤쪽 스크롤 차단 — touchmove 전파 방지
  useEffect(() => {
    const el = document.getElementById('sale-report-root');
    if (!el) return;
    const prevent = (e) => e.stopPropagation();
    el.addEventListener('touchmove', prevent, { passive: false });
    el.addEventListener('wheel', prevent, { passive: false });
    return () => {
      el.removeEventListener('touchmove', prevent);
      el.removeEventListener('wheel', prevent);
    };
  }, []);

  useEffect(() => {
    if (!sale) return;
    setPages((prev) => ({
      ...prev,
      cover: { ...prev.cover, title: prev.cover.title || sale.title || sale.address || '' },
      description: {
        ...prev.description,
        title: prev.description.title || sale.title || '',
        body: prev.description.body || sale.text || '',
      },
      gallery: {
        ...prev.gallery,
        layout: prev.gallery.layout || (
          photos.length >= 6 ? 'D' : photos.length >= 3 ? 'B' : 'A'
        ),
      },
    }));
  }, [sale, photos]);

  // Lambda에서 받아온 지도·지적도 이미지를 location 섹션에 자동 반영
  useEffect(() => {
    if (!reportImages.map && !reportImages.cadastral) return;
    setPages((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        mapImage: reportImages.map || prev.location.mapImage,
        cadastralImage: reportImages.cadastral || prev.location.cadastralImage,
      },
    }));
  }, [reportImages.map, reportImages.cadastral]);


  /* Types that show land info (PAGE 04) */
  const eRandType = sale ? Number(sale.e_rand_type) : null;
  // 토지 정보: 모든 유형에서 표시 (유저가 직접 끌 수 있음)
  const showLand = sale != null;
  const showBasicBuilding = eRandType !== null;

  const imagesPending = pages.location.enabled && reportImages.pending.length > 0;

  const setPage = (key, val) => {
    setPages((prev) => ({ ...prev, [key]: val }));
  };

  const togglePage = (key, enabled) => {
    setPages((prev) => ({ ...prev, [key]: { ...prev[key], enabled } }));
  };

  const triggerDownload = useCallback((url) => {
    window.open(url, '_blank');
  }, []);

  // 발급 시점에 화면 데이터를 수집 — 포함된 것만, 라벨+값 확정
  const collectPdfData = useCallback(() => {
    const isOn = (section, key) => {
      const f = pages[section]?.fields || {};
      return f[key] !== false;
    };

    const filterRows = (rows, section) =>
      rows
        .filter((r) => isOn(section, r.key))
        .map((r) => {
          const raw = r.type === 'toggleBtn' ? resolveToggleBtnLabel(r) : r.value;
          return {
            label: r.label,
            value: r.locale ? fmtNum(raw) : raw,
            unit: r.unit || null,
            sub: r.sub || null,
            full: r.full || false,
            price: r.price || false,
          };
        });

    const data = {
      officeName: pages.contact.fields?.office_name_edit || agent?.office_name || '',
      // 표지
      cover: pages.cover.enabled ? {
        title: pages.cover.title || sale.title || sale.address || '',
        address: pages.cover.fields?.cover_address !== false ? (pages.cover.fields?.cover_address_edit || sale.address || '') : null,
        coverImageUrl: pages.cover.coverPreview || photos?.[0]?.full || null,
      } : null,
      // 매물 개요 — 확정된 rows만, PDF에서 로직 없음
      profile: pages.profile.enabled ? {
        photoUrl: (() => {
          const gp = pages.gallery.photos || photos || [];
          const first = gp[0];
          if (!first) return null;
          return typeof first === 'string' ? first : (first.full || first.thumb || null);
        })(),
        addressRows: filterRows(buildProfileAddressRows(sale, pages.profile), 'profile'),
        priceRows: [buildProfilePdfPriceRow(sale, pages.profile)],
        infoRows: pages.profile.sectionInfo !== false
          ? filterRows(buildProfileInfoRows(sale, pages.profile), 'profile')
          : [],
      } : null,
      // 위치
      location: pages.location.enabled ? {
        mapImage: pages.location.mapImage || null,
        cadastralImage: pages.location.cadastralImage || null,
      } : null,
      // 토지
      land: (showLand && pages.land.enabled) ? {
        rows: filterRows(buildLandRows(sale, pages.land), 'land'),
      } : null,
      // 기본정보/건물정보
      basicBuilding: (showBasicBuilding && pages.basicBuilding.enabled) ? {
        basicRows: filterRows(buildBasicRows(sale, pages.basicBuilding), 'basicBuilding'),
        buildingRows: filterRows(buildBuildingRows(sale, pages.basicBuilding), 'basicBuilding'),
        detailRows: filterRows(buildDetailRows(sale, pages.basicBuilding), 'basicBuilding'),
      } : null,
      // 매물설명
      description: pages.description.enabled ? {
        title: pages.description.title || sale.title || '',
        body: pages.description.body || sale.text || '',
      } : null,
      // 갤러리
      gallery: pages.gallery.enabled ? {
        layout: pages.gallery.layout || 'B',
        photos: (pages.gallery.photos || photos || []).map((p) => typeof p === 'string' ? p : (p?.full || p?.thumb || '')),
      } : null,
      // 연락처
      contact: pages.contact.enabled ? {
        ...buildContactRows(agent, sale, pages.contact),
      } : null,
    };

    return data;
  }, [sale, agent, photos, pages, showLand, showBasicBuilding]);

  const handlePreview = useCallback(() => {
    if (!sale) return;
    const fit = Math.min(1, (window.innerWidth - 48) / 794);
    setPreviewZoom(parseFloat(fit.toFixed(2)));
    setPreviewData(collectPdfData());
  }, [sale, collectPdfData]);

  const handleSubmit = useCallback(async () => {
    if (!sale) return;
    setGenerating(true);
    try {
      const pdfData = collectPdfData();
      console.log('[SaleReport] Lambda payload:', JSON.stringify({ config: pages, data: pdfData }, null, 2));
      const res = await requestReport(saleUuid, pages, pdfData);
      if (!res?.report_uuid) throw new Error('보고서 요청에 실패했습니다.');
      // 요청 성공 → 닫고 www 팝업에서 폴링
      if (onClose) onClose({ reportUuid: res.report_uuid });
    } catch (err) {
      console.error('Report generation failed:', err);
      alert(err.message || 'PDF 생성에 실패했습니다. 다시 시도해주세요.');
      setGenerating(false);
    }
  }, [sale, saleUuid, pages, onClose]);

  const handleDownload = useCallback(() => {
    if (lastDownloadUrl) triggerDownload(lastDownloadUrl);
  }, [lastDownloadUrl, triggerDownload]);

  if (loading) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.page}>
          <div className={styles.loadingWrap}>보고서 데이터를 불러오고 있습니다...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.page}>
          <div className={styles.loadingWrap}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrap}>
      {/* 상단 고정 바 */}
      <header className={styles.stickyBar}>
        <div className={styles.stickyBarInner}>
          <div className={styles.navBrand}>
            보고서 발급
          </div>
          <div className={styles.stickyBarActions}>
            {onClose && (
              <button type="button" className={styles.btn} onClick={onClose}>
                닫기
              </button>
            )}
          </div>
        </div>
      </header>

      <div className={styles.page}>

        {/* Hero */}
        <div className={styles.hero}>
          <div className={styles.heroEyebrow}>새 보고서 발급</div>
          <h1 className={styles.heroTitle}>매물 정보를 입력하시면 매물 보고서가 생성됩니다.</h1>
          <p className={styles.heroLede}>
            입력하신 내용을 바탕으로 표지 · 매물 개요 · 사진 · 설명 · 연락처가 포함된 PDF 보고서가 자동으로 발급됩니다.
          </p>
        </div>

        {/* PAGE 01: 표지 */}
        <PageToggle
          label="표지"
          pageNum={1}
          enabled={pages.cover.enabled}
          onChange={(v) => togglePage('cover', v)}
          first
        />
        <div className={!pages.cover.enabled ? styles.pageOff : undefined}>
          <CoverSection
            config={pages.cover}
            sale={sale}
            photos={photos}
            agent={agent}
            onChange={(v) => setPage('cover', v)}
          />
        </div>

        {/* PAGE 02: 매물 개요 */}
        <PageToggle
          label="매물 개요"
          pageNum={2}
          enabled={pages.profile.enabled}
          onChange={(v) => togglePage('profile', v)}
        />
        <div className={!pages.profile.enabled ? styles.pageOff : undefined}>
          <ProfileSection
            config={pages.profile}
            sale={sale}
            onChange={(v) => setPage('profile', v)}
          />
        </div>

        {/* PAGE 03: 위치 안내 */}
        <PageToggle
          label="위치 안내"
          pageNum={3}
          enabled={pages.location.enabled}
          onChange={(v) => togglePage('location', v)}
        />
        <div className={!pages.location.enabled ? styles.pageOff : undefined}>
          <LocationSection
            config={pages.location}
            imagePending={reportImages.pending}
          />
        </div>

        {/* PAGE 04: 토지 정보 */}
        {showLand && (
          <>
            <PageToggle
              label="토지 정보"
              pageNum={4}
              enabled={pages.land.enabled}
              onChange={(v) => togglePage('land', v)}
            />
            <div className={!pages.land.enabled ? styles.pageOff : undefined}>
              <LandSection
                config={pages.land}
                sale={sale}
                onChange={(v) => setPage('land', v)}
              />
            </div>
          </>
        )}

        {/* PAGE 05: 기본정보 · 건물정보 */}
        {showBasicBuilding && (
          <>
            <PageToggle
              label="기본정보 · 건물정보"
              pageNum={5}
              enabled={pages.basicBuilding.enabled}
              onChange={(v) => togglePage('basicBuilding', v)}
            />
            <div className={!pages.basicBuilding.enabled ? styles.pageOff : undefined}>
              <BasicBuildingSection
                config={pages.basicBuilding}
                sale={sale}
                onChange={(v) => setPage('basicBuilding', v)}
              />
            </div>
          </>
        )}

        {/* PAGE 06: 매물 설명 */}
        <PageToggle
          label="매물 설명"
          pageNum={6}
          enabled={pages.description.enabled}
          onChange={(v) => togglePage('description', v)}
        />
        <div className={!pages.description.enabled ? styles.pageOff : undefined}>
          <DescriptionSection
            config={pages.description}
            sale={sale}
            onChange={(v) => setPage('description', v)}
          />
        </div>

        {/* PAGE 07: 매물 사진 */}
        <PageToggle
          label="매물 사진"
          pageNum={7}
          enabled={pages.gallery.enabled}
          onChange={(v) => togglePage('gallery', v)}
        />
        <div className={!pages.gallery.enabled ? styles.pageOff : undefined}>
          <GallerySection
            config={pages.gallery}
            photos={photos}
            onChange={(v) => setPage('gallery', v)}
          />
        </div>

        {/* PAGE 08: 문의 · 연락처 */}
        <PageToggle
          label="문의 · 연락처"
          pageNum={8}
          enabled={pages.contact.enabled}
          onChange={(v) => togglePage('contact', v)}
        />
        <div className={!pages.contact.enabled ? styles.pageOff : undefined}>
          <ContactSection
            config={pages.contact}
            agent={agent}
            sale={sale}
            onChange={(v) => setPage('contact', v)}
          />
        </div>

        {/* Footer */}
        <div className={styles.foot}>
          <p className={styles.footNote}>
            발급 버튼을 누르면 입력하신 내용을 기반으로 표지 · 매물 개요 · 사진 · 설명 · 연락처 페이지가 자동 구성된 PDF가 생성됩니다.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={generating || imagesPending}
              onClick={() => handleSubmit()}
            >
              {generating ? '생성 중...' : imagesPending ? '지도 로딩 중...' : '보고서 발급'}
            </button>
          </div>
        </div>


        <div className={styles.pageBottom}>
          <span>Property Report @ disco.re</span>
          <span>2026 · 07</span>
        </div>

        {/* 미리보기 오버레이 */}
        {previewData && (() => {
          const now = new Date();
          const dl = `${now.getFullYear()} · ${String(now.getMonth() + 1).padStart(2, '0')}`;
          const yl = `Property · ${now.getFullYear()}`;
          const on = previewData.officeName;
          const pageCard = { borderRadius: 8, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' };
          return (
            <div className={styles.previewOverlay}>
              <style>{PDF_STYLES}</style>

              {/* 플로팅 컨트롤 */}
              <div className={styles.previewCtrl}>
                <button
                  className={styles.previewZoomBtn}
                  disabled={previewZoom <= 0.3}
                  onClick={() => setPreviewZoom(z => Math.max(0.3, parseFloat((z - 0.1).toFixed(2))))}
                >−</button>
                <span className={styles.previewZoomLabel}>{Math.round(previewZoom * 100)}%</span>
                <button
                  className={styles.previewZoomBtn}
                  disabled={previewZoom >= 2}
                  onClick={() => setPreviewZoom(z => Math.min(2, parseFloat((z + 0.1).toFixed(2))))}
                >+</button>
                <div className={styles.previewCtrlSep} />
                <button className={styles.previewCloseBtn} onClick={() => setPreviewData(null)}>✕</button>
              </div>

              {/* 스크롤 영역 */}
              <div className={styles.previewScroll}>
                <div className={styles.previewPages} style={{ zoom: previewZoom }}>
                  {previewData.cover && <div style={pageCard}><PdfCover data={previewData.cover} officeName={on} dateLabel={dl} yearLabel={yl} /></div>}
                  {previewData.profile && <div style={pageCard}><PdfProfile data={previewData.profile} officeName={on} dateLabel={dl} /></div>}
                  {previewData.location && <div style={pageCard}><PdfLocation data={previewData.location} officeName={on} dateLabel={dl} /></div>}
                  {previewData.land && <div style={pageCard}><PdfLand data={previewData.land} officeName={on} dateLabel={dl} /></div>}
                  {previewData.basicBuilding && <div style={pageCard}><PdfBuilding data={previewData.basicBuilding} officeName={on} dateLabel={dl} /></div>}
                  {previewData.description && (descPreviewChunks || [(previewData.description.body || '').split('\n').filter(Boolean)]).map((chunk, ci) => (
                    <div key={ci} style={pageCard}><PdfDescription data={{ ...previewData.description, paragraphs: chunk }} pageIndex={ci} officeName={on} dateLabel={dl} /></div>
                  ))}
                  {previewData.gallery && (() => {
                    const { layout, photos: gPhotos } = previewData.gallery;
                    const perPage = GALLERY_PER_PAGE[layout] || 3;
                    const chunks = [];
                    for (let i = 0; i < gPhotos.length; i += perPage) chunks.push(gPhotos.slice(i, i + perPage));
                    return chunks.map((chunk, pi) => (
                      <div key={pi} style={pageCard}><PdfGallery data={{ layout, photos: chunk }} startIndex={pi * perPage} officeName={on} dateLabel={dl} /></div>
                    ));
                  })()}
                  {previewData.contact && <div style={pageCard}><PdfContact data={previewData.contact} officeName={on} dateLabel={dl} /></div>}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default SaleReportPage;
